import { useState, useRef, useCallback } from 'react';
import { Upload, X, Loader, Image as ImageIcon } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import type { Team } from '../types/employee';
import { supabase } from '../lib/supabase';
import TeamIcon from './TeamIcon';

// Curated subset of Lucide icons suitable for team badges
const LUCIDE_ICON_NAMES = [
  'Cat', 'Dog', 'Bird', 'Fish', 'Rabbit', 'Turtle', 'Squirrel', 'Bug',
  'Code2', 'Code', 'Terminal', 'Cpu', 'Database', 'Server', 'Globe', 'Wifi',
  'Zap', 'Flame', 'Rocket', 'Star', 'Heart', 'Trophy', 'Medal', 'Crown',
  'Coffee', 'Beer', 'Pizza', 'Cake', 'Cookie', 'Apple',
  'Music', 'Gamepad2', 'Palette', 'Camera', 'Film',
  'Mountain', 'Waves', 'TreePine', 'Flower2', 'Sun', 'Moon',
  'Shield', 'Sword', 'Swords', 'Target', 'Crosshair',
  'Car', 'Bike', 'Plane', 'Boat', 'Train',
  'Microscope', 'FlaskConical', 'Atom', 'Dna', 'Brain',
  'BookOpen', 'GraduationCap', 'Lightbulb', 'Wrench', 'Hammer',
  'Users', 'User', 'UserCheck', 'Handshake', 'MessageSquare',
  'Leaf', 'Recycle', 'Wind', 'Droplets', 'Snowflake',
  'Diamond', 'Gem', 'Sparkles', 'Hexagon', 'Triangle',
];

const EMOJI_LIST = [
  '🦀', '💎', '🥤', '🫙', '🧡', '🔥', '⚡', '🚀', '⭐', '❤️',
  '🏆', '🥇', '👑', '☕', '🍺', '🍕', '🎵', '🎮', '🎨', '📸',
  '🏔️', '🌊', '🌲', '🌸', '☀️', '🌙', '🛡️', '⚔️', '🎯', '🚗',
  '✈️', '🚀', '🔬', '🧪', '⚛️', '🧬', '🧠', '📚', '💡', '🔧',
  '🤝', '💬', '🌱', '♻️', '💧', '❄️', '✨', '🦁', '🐯', '🦊',
  '🐧', '🦅', '🐉', '🦄', '🐝', '🌍', '🎸', '🥊', '🎭', '🏄',
  '🧩', '🎲', '🃏', '🎰', '🌮', '🍜', '🍣', '🍎', '🥑', '🍇',
  '🧋', '🍵', '🧃', '🥂', '🍾', '🎉', '🎊', '🪄', '🌈', '🔮',
];

type IconType = Team['iconType'];

interface TeamIconPickerProps {
  value: { iconType: IconType; iconValue: string };
  onChange: (value: { iconType: IconType; iconValue: string }) => void;
  teamId?: string;
}

type Tab = 'lucide' | 'emoji' | 'image';

const TeamIconPicker = ({ value, onChange, teamId }: TeamIconPickerProps) => {
  const [tab, setTab] = useState<Tab>(value.iconType === 'image' ? 'image' : value.iconType === 'emoji' ? 'emoji' : 'lucide');
  const [lucideSearch, setLucideSearch] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredIcons = lucideSearch.trim()
    ? LUCIDE_ICON_NAMES.filter(n => n.toLowerCase().includes(lucideSearch.toLowerCase()))
    : LUCIDE_ICON_NAMES;

  const handleUpload = useCallback(async (file: File) => {
    setUploadError(null);
    if (!file.type.startsWith('image/')) { setUploadError('Please select an image file.'); return; }
    if (file.size > 2 * 1024 * 1024) { setUploadError('Image must be under 2 MB.'); return; }

    setIsUploading(true);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? 'png';
      const key = teamId ?? `tmp-${Date.now()}`;
      const path = `${key}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('team-icons')
        .upload(path, file, { upsert: true, contentType: file.type });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from('team-icons').getPublicUrl(path);
      const publicUrl = `${data.publicUrl}?t=${Date.now()}`;
      onChange({ iconType: 'image', iconValue: publicUrl });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setIsUploading(false);
    }
  }, [teamId, onChange]);

  const preview = value.iconType !== 'image' || value.iconValue
    ? <TeamIcon team={value} size={20} />
    : null;

  return (
    <div className="border border-bolt-border rounded-xl overflow-hidden bg-bolt-bg">
      {/* Tabs */}
      <div className="flex border-b border-bolt-border">
        {([['lucide', 'Icons'], ['emoji', 'Emoji'], ['image', 'Upload']] as [Tab, string][]).map(([t, label]) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`flex-1 py-2 text-xs font-medium transition-colors ${
              tab === t
                ? 'bg-bolt-surface text-bolt-text border-b-2 border-primary-500'
                : 'text-bolt-textSubtle hover:text-bolt-textMuted hover:bg-bolt-surfaceHover'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Current selection preview */}
      {value.iconValue && (
        <div className="flex items-center gap-2 px-3 py-2 bg-bolt-surface border-b border-bolt-border">
          <span className="text-xs text-bolt-textSubtle">Selected:</span>
          <span className="text-bolt-textMuted">{preview}</span>
          <span className="text-xs text-bolt-textMuted">{value.iconValue}</span>
          <button
            type="button"
            onClick={() => onChange({ iconType: 'lucide', iconValue: '' })}
            className="ml-auto text-bolt-textSubtle hover:text-error-400 transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Tab content */}
      <div className="p-3">
        {tab === 'lucide' && (
          <div>
            <input
              type="text"
              placeholder="Search icons..."
              value={lucideSearch}
              onChange={e => setLucideSearch(e.target.value)}
              className="form-input mb-3 text-xs py-1.5"
            />
            <div className="grid grid-cols-8 gap-1 max-h-40 overflow-y-auto">
              {filteredIcons.map(name => {
                const Icon = (LucideIcons as Record<string, React.ComponentType<{ size?: number; className?: string }>>)[name];
                if (!Icon) return null;
                const isSelected = value.iconType === 'lucide' && value.iconValue === name;
                return (
                  <button
                    key={name}
                    type="button"
                    title={name}
                    onClick={() => onChange({ iconType: 'lucide', iconValue: name })}
                    className={`flex items-center justify-center w-8 h-8 rounded-md transition-colors ${
                      isSelected
                        ? 'bg-primary-600/30 text-primary-400 ring-1 ring-primary-500'
                        : 'text-bolt-textSubtle hover:bg-bolt-surfaceHover hover:text-bolt-textMuted'
                    }`}
                  >
                    <Icon size={16} />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {tab === 'emoji' && (
          <div className="grid grid-cols-10 gap-1 max-h-48 overflow-y-auto">
            {EMOJI_LIST.map(emoji => {
              const isSelected = value.iconType === 'emoji' && value.iconValue === emoji;
              return (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => onChange({ iconType: 'emoji', iconValue: emoji })}
                  className={`flex items-center justify-center w-8 h-8 rounded-md text-base transition-colors ${
                    isSelected
                      ? 'bg-primary-600/30 ring-1 ring-primary-500'
                      : 'hover:bg-bolt-surfaceHover'
                  }`}
                >
                  {emoji}
                </button>
              );
            })}
          </div>
        )}

        {tab === 'image' && (
          <div>
            <div className="flex items-start gap-3">
              {/* Preview */}
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-bolt-surface border border-bolt-border flex items-center justify-center flex-shrink-0">
                {value.iconType === 'image' && value.iconValue ? (
                  <img src={value.iconValue} alt="Team icon" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="h-5 w-5 text-bolt-textSubtle" />
                )}
                {isUploading && (
                  <div className="absolute inset-0 bg-bolt-bg/70 flex items-center justify-center rounded-lg">
                    <Loader className="h-4 w-4 text-primary-400 animate-spin" />
                  </div>
                )}
              </div>

              {/* Dropzone */}
              <div
                className={`flex-1 border-2 border-dashed rounded-lg p-3 flex flex-col items-center justify-center gap-1.5 cursor-pointer transition-colors min-h-14 ${
                  isDragging
                    ? 'border-primary-500 bg-primary-600/10'
                    : 'border-bolt-border hover:border-bolt-borderLight hover:bg-bolt-surfaceHover'
                }`}
                onClick={() => inputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={e => { e.preventDefault(); setIsDragging(false); if (e.dataTransfer.files[0]) handleUpload(e.dataTransfer.files[0]); }}
              >
                <Upload className="h-3.5 w-3.5 text-bolt-textSubtle" />
                <p className="text-xs text-bolt-textMuted text-center">
                  <span className="text-primary-400 font-medium">Click</span> or drag image
                </p>
                <p className="text-[10px] text-bolt-textSubtle">JPEG, PNG, WebP — max 2 MB</p>
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={e => { if (e.target.files?.[0]) handleUpload(e.target.files[0]); }}
                />
              </div>
            </div>
            {uploadError && <p className="mt-2 text-xs text-error-400">{uploadError}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamIconPicker;
